import { Input } from '@mantine/core';
import { RichTextEditor } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { ReactNode } from 'react';
import { FieldValues, useController } from 'react-hook-form';

import { FormControlInput } from '@/core/components';

export interface ContentEditableControlProps<T extends FieldValues> extends FormControlInput<T> {
  withAsterisk?: boolean;

  label: string;
  description?: string;

  isDisabled?: boolean;

  leftSection?: ReactNode;
  leftSectionWidth?: string;
}

export function ContentEditableControl<T extends FieldValues>(props: ContentEditableControlProps<T>) {
  const { withAsterisk, label, description, name, isSubmitting, isDisabled } = props;
  const {
    field,
    fieldState: { error },
  } = useController({ name });

  const editor = useEditor({
    extensions: [StarterKit],
    content: field.value ?? '',
    onUpdate: ({ editor: ed }) => {
      if (!isSubmitting && !isDisabled) {
        field.onChange(ed.getHTML());
      }
    },
  });

  return (
    <Input.Wrapper
      withAsterisk={withAsterisk}
      label={label}
      description={description}
      error={error?.message}
    >
      <RichTextEditor
        editor={editor}
        variant="subtle"
      >
        <RichTextEditor.Toolbar
          sticky
          stickyOffset={60}
        >
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Bold />
            <RichTextEditor.Italic />
            <RichTextEditor.Underline />
            <RichTextEditor.Strikethrough />
            <RichTextEditor.ClearFormatting />
          </RichTextEditor.ControlsGroup>
        </RichTextEditor.Toolbar>

        <RichTextEditor.Content />
      </RichTextEditor>
    </Input.Wrapper>
  );
}
